import React, { useState } from 'react';
import { Save, Play, Pause, Check, ArrowLeft } from 'lucide-react';
import * as Tone from 'tone';
import { pitchTrainingService } from '../../../services/pitchTrainingService';

/**
 * Track Preview Component
 * Preview selected track and save to database
 */
export default function TrackPreview({ midiData, selectedTrack, onSave, onBack }) {
  const [trackName, setTrackName] = useState(selectedTrack?.name);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handlePlayPreview = async () => {
    if (isPlaying) {
      Tone?.Transport?.stop();
      setIsPlaying(false);
      return;
    }

    try {
      await Tone?.start();

      const synth = new Tone.PolySynth(Tone.Synth)?.toDestination();
      const track = midiData?.midi?.tracks?.[selectedTrack?.index];
      
      const now = Tone?.now();
      track?.notes?.forEach(note => {
        synth?.triggerAttackRelease(
          Tone?.Frequency(note?.midi, 'midi'),
          note?.duration,
          now + note?.time
        );
      });

      setIsPlaying(true);

      // Auto-stop after duration
      setTimeout(() => {
        setIsPlaying(false);
        synth?.dispose();
      }, track?.duration * 1000 + 500);
    } catch (err) {
      console.error('Error playing preview:', err);
      setError('Failed to play preview');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Calculate tempo from MIDI
      const tempoBpm = midiData?.midi?.header?.tempos?.[0]?.bpm || 120;

      // Prepare metadata
      const metadata = {
        trackName,
        totalNotes: selectedTrack?.noteCount,
        noteRangeLow: selectedTrack?.range?.low,
        noteRangeHigh: selectedTrack?.range?.high,
        durationSeconds: selectedTrack?.duration,
        tempoBpm
      };

      // Upload to service
      const result = await pitchTrainingService?.uploadMidiTrack(midiData?.file, metadata);

      if (result?.error) {
        throw new Error(result.error);
      }

      setSaveSuccess(true);
      
      // Wait a moment then callback
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (err) {
      console.error('Error saving track:', err);
      setError(err?.message || 'Failed to save track');
    } finally {
      setIsSaving(false);
    }
  };

  const midiToNoteName = (midi) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(midi / 12) - 1;
    const note = noteNames?.[midi % 12];
    return `${note}${octave}`;
  };

  if (saveSuccess) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Track Saved Successfully! 🎉</h2>
          <p className="text-gray-600 mb-6">The melody is now available in Pitch Coach</p>
          <button
            onClick={onSave}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            Upload Another Track
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Preview & Save Track</h2>
      {/* Track Name Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Track Name
        </label>
        <input
          type="text"
          value={trackName}
          onChange={(e) => setTrackName(e?.target?.value)}
          placeholder="Enter a descriptive name"
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
        />
      </div>
      {/* Track Info */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-bold text-lg text-gray-800 mb-4">Track Information</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-1">Total Notes</p>
            <p className="font-bold text-gray-800">{selectedTrack?.noteCount}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Note Range</p>
            <p className="font-bold text-gray-800">
              {midiToNoteName(selectedTrack?.range?.low)} - {midiToNoteName(selectedTrack?.range?.high)}
            </p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Duration</p>
            <p className="font-bold text-gray-800">{Math.round(selectedTrack?.duration)} seconds</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Average Pitch</p>
            <p className="font-bold text-gray-800">{midiToNoteName(Math.round(selectedTrack?.avgPitch))}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Tempo</p>
            <p className="font-bold text-gray-800">{midiData?.midi?.header?.tempos?.[0]?.bpm || 120} BPM</p>
          </div>
        </div>
      </div>
      {/* Preview Button */}
      <button
        onClick={handlePlayPreview}
        disabled={isPlaying}
        className="w-full mb-6 py-4 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
      >
        {isPlaying ? (
          <>
            <Pause className="w-6 h-6" />
            Playing Preview...
          </>
        ) : (
          <>
            <Play className="w-6 h-6" />
            Play Preview
          </>
        )}
      </button>
      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onBack}
          className="flex-1 py-4 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving || !trackName?.trim()}
          className="flex-1 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-6 h-6" />
              Save Track
            </>
          )}
        </button>
      </div>
    </div>
  );
}